<template>
    <div class="documents-table-wrap">
        <DocumentModal 
            :show-modal-on="['EditDocument', 'AddDocument', 'ShareDocument', 'ShareDocuments']"
            @created="onDocumentCreated"
            @updated="onDocumentUpdated"
            @deleted="onDocumentDeleted" />

        <FilterBar
            cookieName="DocumentsFilterOptionsVue"
            :mainFilters="['SearchFilter', 'DateRangeFilter', 'DocumentTypeFilter']"
            :secondaryFilters="[]"
            filterBarType="documents"
            @change="filtersChanged">
            <template v-slot:right-bar>
                <TotalAmountSpace :numberOfFiles="numberOfFiles" />
            </template>
        </FilterBar>
        
        <div class="content--inner">
            <b-table
                :fields="fields"
                :items="items"
                :class="['trips-table gk-table gk-table--responsive gk-table--striped gk-table--hover']"
                :busy="loading"
                @row-clicked="documentRowClicked"
                show-empty
                table-busy
            >
                <template #empty="">
                    <EmptyState>
                        <template v-slot:image>
                            <img src="/static/img/empty-states/empty-state-cost.png">
                        </template>
                        <template v-slot:title>
                            {{ $gettext('We couldn\'t find any document') }}
                        </template>
                        <template v-slot:content>
                            {{ $gettext('Start by uploading a new one or try updating your filters.') }}
                        </template>
                        <template v-slot:cta>
                            <GekkoButton 
                                :text="$gettext('Upload')"
                                :to="{ name: 'AddDocument' }"
                                icon="fa-solid fa-upload" />
                        </template>
                    </EmptyState>
                </template>
                <template #table-busy="">
                    <LoadingSpinner min-height />
                </template>

                <template #cell(actions)="data">
                    <div class="text-right">
                        <b-dropdown class="action-dropdown" no-caret>
                            <template #button-content>
                                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
                            </template>
                            <b-dropdown-item @click="download(data.item)"><font-awesome-icon icon="fa-solid fa-download" /> <translate>Download</translate></b-dropdown-item>
                        </b-dropdown>
                    </div>
                </template>
                <template slot="bottom-row" v-if="nextUrl">
                    <td :colspan="fields.length" class="table-load-more">
                        <GekkoButton 
                            v-if="nextUrl && !fetchingData" 
                            @click="handleScroll()"
                            id="load_more_button" 
                            class="text-info"
                            variant="link" 
                            :text="$gettext('Load more documents')" 
                        />
                        <LoadingSpinner v-if="fetchingData" />
                    </td>
                </template>
            </b-table>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import FilterBar from "@/components/list_filters/FilterBar";
import GekkoButton from "@/components/GekkoButton";
import TotalAmountSpace from "@/components/documents/TotalAmountSpace";
import DocumentService from "@/store/services/document.service";
import DocumentModal from "@/components/documents/DocumentModal";
import EmptyState from "@/components/ui/EmptyState";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
    name: "DocumentsTable",
    components: { FilterBar, GekkoButton, TotalAmountSpace, DocumentModal, EmptyState, LoadingSpinner },
    data () {
        return {
            items: [],
            itemCount: 0,
            fields: [
                {key: "title", label: this.$gettext('Title')},
                {key: "type_name", label: this.$gettext('Type')},
                // {key: "file_size_text", label: this.$gettext('File size')},
                {key: "valid_from", label: this.$gettext('Date')},
                {key: "customer_name", label: this.$gettext('Customer name'), class: 'unimportant-column'},
                {key: "project_name", label: this.$gettext('Project name'), class: 'unimportant-column'},
                {key: "actions", label: ""}
            ],
            filters: {},
            loading: true,
            fetchingData: true,
            nextUrl: null
        }
    },
    computed: {
        ...mapGetters ({ 
            currentCompany: 'main/getCurrentCompany'
        }),
        numberOfFiles () {
            return this.itemCount
        }
    },
    created () {
        window.addEventListener('scroll', this.handleScroll);
    },
    destroyed () {
        window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        getDocuments () {
            this.fetchingData = true
            this.loading = true

            this.filters.extended = "True"
            this.filters.company_profile = this.currentCompany.id

            DocumentService.list(this.filters).then((response) => {
                this.items = response.data.results
                this.itemCount = response.data.count
                this.items.forEach((item) => {
                    item.file_size_text = DocumentService.getFileSizeText(item.file_size);
                    item.customer_name = item.customer?.name;
                    item.project_name = item.project?.name;
                });
                this.nextUrl = response.data.next
                this.fetchingData = false
                this.loading = false
            });
        },
        filtersChanged(filters) {
            this.filters = filters;
            this.filters.search_by_title = filters.search;
            this.getDocuments();
        },
        documentRowClicked(item) {
            this.$router.push({name: 'EditDocument', params: {documentId: item.id}});
        },
        download(item) {
            DocumentService.download(item.id);
        },
        onDocumentCreated(item) {
            item.file_size_text = DocumentService.getFileSizeText(item.file_size);
            item.customer_name = item.customer?.name;
            item.project_name = item.project?.name;
            this.items.splice(0, 0, item);
            this.$store.commit("filterStore/SET_UPDATE_FILTER_DOCUMENTS");
        },
        onDocumentUpdated(item) {
            for (let i in this.items) {
                if (this.items[i].id === item.id) {
                    this.$set(this.items, i, item);
                    this.items[i].file_size_text = DocumentService.getFileSizeText(item.file_size);
                    break;
                }
            }
            this.$store.commit("filterStore/SET_UPDATE_FILTER_DOCUMENTS");
        },
        onDocumentDeleted(docId) {
            for (let i in this.items) {
                if (this.items[i].id === docId) {
                    this.items.splice(i, 1);
                    break;
                }
            }
            this.$store.commit("filterStore/SET_UPDATE_FILTER_DOCUMENTS");
        },
        isInViewport(element_id) {
            let element = document.getElementById(element_id)
            let rect = element.getBoundingClientRect();
            let html = document.documentElement;
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || html.clientHeight) &&
                rect.right <= (window.innerWidth || html.clientWidth)
            );
        },
        handleScroll() {
            if (!this.fetchingData && this.nextUrl) {
                
                let loadNewDocuments = this.isInViewport('load_more_button');
                if (loadNewDocuments) {
                    this.fetchingData = true;
                    
                    DocumentService.listNextPage(this.nextUrl).then((response) => {
                        this.fetchingData = false;

                        this.items = this.items.concat(response.data.results);
                        this.items.forEach((item) => {
                            item.file_size_text = DocumentService.getFileSizeText(item.file_size);
                            if (item.customer)
                                item.customer_name = item.customer.name;
                            if (item.project)
                                item.project_name = item.project.name;
                        });
                        this.nextUrl = response.data.next;
                    });
                }
            }
        }
    }
}
</script>

<style lang="scss">
.documents-table-wrap {
    .content--inner {
        min-height: 360px;
    }
}
@media(max-width: 414px) {
    .unimportant-column {
        display: none !important;
    }
}
</style>